import {
  Component,
  ElementRef,
  AfterViewInit,
  ChangeDetectionStrategy,
  ViewEncapsulation,
  ViewChild,
  TemplateRef,
  ViewContainerRef,
  Input,
  OnDestroy,
  ChangeDetectorRef,
  OnInit,
} from '@angular/core';
import { DragDrop, DragRef } from '@angular/cdk/drag-drop';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';

import { PhxDropListComponent } from '../../drop-list/phx-drop-list.component';
import { PhxPlaceholderStyles } from '../../phx-drag-and-drop.types';

@Component({
  selector: 'phx-drag-item',
  templateUrl: './phx-drag-item.component.html',
  styleUrls: ['./phx-drag-item.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  encapsulation: ViewEncapsulation.None,
  host: {
    class: 'phx-drag-item',
    '[class.phx-drag-item-no-handle]': '!hasDragHandle && !isViewMode',
  },
})
export class PhxDragItemComponent implements OnInit, AfterViewInit, OnDestroy {
  /**
   * Input property to define the CSS class for the placeholder.
   */
  @Input() phxPlaceholderClass: string;

  /**
   * Input property to define the CSS class for the dragging state.
   */
  @Input() phxDraggingClass: string;

  /**
   * Input property to determine if the drag should be constrained within the parent element, i,e; PhxDropListComponent.
   */
  @Input() phxDragWithinParent: boolean = false;

  @ViewChild('dragItemPlacehoder', { read: TemplateRef }) placeholderTemplate: TemplateRef<any>;

  public isViewMode: boolean = false;
  public hasDragHandle: boolean = false;
  public placeholderStyles: PhxPlaceholderStyles;

  private _dragRef: DragRef;
  private _destroy$: Subject<void> = new Subject();

  constructor(
    private _dragDrop: DragDrop,
    private _elementRef: ElementRef<HTMLElement>,
    private _phxDropListComponent: PhxDropListComponent<any>,
    private _vcr: ViewContainerRef,
    private _cdr: ChangeDetectorRef
  ) {}

  ngOnInit(): void {
    this.initializeDragRef();
  }

  ngAfterViewInit() {
    this.setupPlaceholder();
    this.setupDragBoundry();
    this.subscribeDragEvents();
    this.subscribeViewModeStream();
  }

  ngOnDestroy() {
    this._destroy$.next();
    this._destroy$.complete();
    this._phxDropListComponent.unregisterDragItem(this._dragRef);
    this._dragRef.dispose();
  }

  /**
   * Initializes the drag reference for the component and registering it in drop list in parent component.
   */
  private initializeDragRef(): void {
    this._dragRef = this._dragDrop.createDrag(this._elementRef);
    this._dragRef.previewClass = this.phxDraggingClass || 'phx-drag-item-dragging';
    this._phxDropListComponent.registerDragItem(this._dragRef);
  }

  /**
   * Sets up the placeholder for the drag item.
   */
  private setupPlaceholder(): void {
    this.setPlaceholderStyles();
    this._dragRef.withPlaceholderTemplate({
      template: this.placeholderTemplate,
      context: null,
      viewContainer: this._vcr,
    });
  }

  /**
   * Sets up the drag boundary for the component.
   * If the `phxType` is 'list' or `phxDragWithinParent` is true,
   * the drag boundary is set to the native element of PhxDropListComponent.
   */
  private setupDragBoundry(): void {
    if (this.phxDragWithinParent) {
      this._dragRef.withBoundaryElement(this._phxDropListComponent.nativeElement);
    }
  }

  /**
   * Subscribes to drag events and applies CSS classes to the element when dragging starts and ends.
   */
  private subscribeDragEvents(): void {
    this._dragRef.beforeStarted.pipe(takeUntil(this._destroy$)).subscribe(() => {
      this.setPlaceholderStyles();
    });
  }

  /**
   * Subscribes to the isViewMode$ stream and updates the component's state accordingly.
   */
  private subscribeViewModeStream(): void {
    this._phxDropListComponent.isViewMode$.pipe(takeUntil(this._destroy$)).subscribe((isViewMode) => {
      this.isViewMode = isViewMode;
      this._dragRef.disabled = isViewMode;
      this.setPlaceholderStyles();
      this._cdr.detectChanges();
    });
  }

  /**
   * Retrieves the styles of drag element to apply on placeholder.
   */
  private setPlaceholderStyles(): void {
    const draggedElement = this._elementRef.nativeElement;
    const draggedElementStyles = window.getComputedStyle(draggedElement);

    const placeholderStyles: PhxPlaceholderStyles = {
      height: `${draggedElement.offsetHeight}px`,
    };

    if (draggedElementStyles.borderRadius !== '0px') placeholderStyles.borderRadius = draggedElementStyles.borderRadius;

    this.placeholderStyles = placeholderStyles;
  }

  public registerDragHandle(elementRef: ElementRef<HTMLElement>): void {
    this._dragRef.withHandles([elementRef]);
    this.hasDragHandle = true;
    this._cdr.detectChanges();
  }

  public unregisterDragHandle(): void {
    this._dragRef.withHandles([]);
    this.hasDragHandle = false;
    this._cdr.detectChanges();
  }
}
